import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Clock, Check, X, Trash2, Loader2, Phone, Dog, CalendarDays, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AdminLayout } from "./AdminLayout";
import { MonthCalendar, toKey } from "../components/calendar/MonthCalendar";
import { bookingsAdminApi, formatApiErrorDetail, eur } from "./api";
import { fmtDatePt } from "../data/site";
import { ClientPicker } from "./ClientPicker";

const SERVICES = ["Banho", "Tosquia", "Banho + Tosquia", "Corte de unhas", "Grooming completo"];
const DURATIONS = [
  { value: 30, label: "30 min" },
  { value: 60, label: "1h" },
  { value: 90, label: "1h30" },
  { value: 120, label: "2h" },
];

function durLabel(mins) {
  const found = DURATIONS.find((d) => d.value === mins);
  if (found) return found.label;
  const h = Math.floor(mins / 60), m = mins % 60;
  return h ? `${h}h${m ? m : ""}` : `${m} min`;
}

function endTime(start, duration) {
  const [h, m] = start.split(":").map(Number);
  const total = h * 60 + m + (duration || 30);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

const STATUS_META = {
  pending: { label: "Pendente", cls: "bg-[#fff4e5] text-[#b26a00] border-[#f0c48a]" },
  confirmed: { label: "Confirmada", cls: "bg-[#e6f7fb] text-[#0077a3] border-[#a8dced]" },
  cancelled: { label: "Cancelada", cls: "bg-[#fdeaf2] text-[#E20074] border-[#f4b6d3]" },
  done: { label: "Concluída", cls: "bg-[#eaf6ea] text-[#2e7d32] border-[#b6dcb6]" },
};

export default function AdminCalendar() {
  const [viewDate, setViewDate] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedKey, setSelectedKey] = useState(null);
  const [monthBookings, setMonthBookings] = useState([]);
  const [daySlots, setDaySlots] = useState(null);
  const [loadingDay, setLoadingDay] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [presetTime, setPresetTime] = useState("");
  const [editing, setEditing] = useState(null);

  const monthRange = useCallback(() => {
    const y = viewDate.getFullYear(), m = viewDate.getMonth();
    return { from: toKey(new Date(y, m, 1)), to: toKey(new Date(y, m + 1, 0)) };
  }, [viewDate]);

  const loadMonth = useCallback(async () => {
    const { from, to } = monthRange();
    try {
      const res = await bookingsAdminApi.list({ from_date: from, to_date: to });
      setMonthBookings(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [monthRange]);

  useEffect(() => { loadMonth(); }, [loadMonth]);

  const loadDay = useCallback(async (key) => {
    setLoadingDay(true);
    try {
      const res = await bookingsAdminApi.availability(key);
      setDaySlots(res.data);
    } catch {
      setDaySlots({ closed: false, slots: [] });
    } finally {
      setLoadingDay(false);
    }
  }, []);

  const selectDay = (key) => { setSelectedKey(key); loadDay(key); };

  const countByDate = monthBookings.reduce((acc, b) => {
    if (b.status !== "cancelled") acc[b.date] = (acc[b.date] || 0) + 1;
    return acc;
  }, {});

  const bookingFor = (time) =>
    monthBookings.find((b) => b.date === selectedKey && b.time === time && b.status !== "cancelled");

  const refresh = async () => { await loadMonth(); if (selectedKey) await loadDay(selectedKey); };

  const openAdd = (time) => { setPresetTime(time); setAddOpen(true); };

  const createBooking = async (form) => {
    try {
      await bookingsAdminApi.create({ ...form, date: selectedKey, time: presetTime });
      toast.success("Marcação adicionada");
      setAddOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const editBooking = async (form) => {
    try {
      await bookingsAdminApi.update(editing.id, { ...form, date: selectedKey });
      toast.success("Marcação atualizada");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    try { await bookingsAdminApi.setStatus(id, status); toast.success("Atualizado"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await bookingsAdminApi.remove(id); toast.success("Eliminada"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="font-display font-black text-4xl tracking-tight">Calendário de Marcações</h1>
        <p className="text-[#525252] text-sm mt-1">Clique num dia para ver os horários e adicionar marcações.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Calendar */}
        <div className="bg-white border border-[#D4D4D4] rounded-xl p-5">
          <MonthCalendar
            viewDate={viewDate}
            onPrev={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
            onNext={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
            selectedKey={selectedKey}
            onSelectDay={selectDay}
            countByDate={countByDate}
          />
          <div className="flex items-center gap-4 mt-4 text-xs text-[#525252]">
            <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[#E20074]" /> Com marcações</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full ring-1 ring-[#00B4D8]" /> Hoje</span>
          </div>
        </div>

        {/* Day panel */}
        <div className="bg-white border border-[#D4D4D4] rounded-xl p-5" data-testid="day-panel">
          {!selectedKey ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16 text-[#525252]">
              <CalendarDays size={40} className="text-[#D4D4D4] mb-3" />
              Selecione um dia no calendário.
            </div>
          ) : (
            <>
              <h2 className="font-display font-bold text-xl capitalize mb-4">{fmtDatePt(selectedKey)}</h2>
              {loadingDay ? (
                <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#E20074]" /></div>
              ) : daySlots?.closed ? (
                <p className="text-[#E20074] py-8 text-center" data-testid="day-closed">A loja está encerrada neste dia.</p>
              ) : (
                <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1" data-testid="day-slots">
                  {daySlots?.slots.map((s) => {
                    const b = bookingFor(s.time);
                    if (!b && !s.available) {
                      return (
                        <div key={s.time} data-testid={`busy-slot-${s.time}`}
                          className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-transparent bg-[#f0eeea] text-[#a3a3a3]">
                          <span className="flex items-center gap-2"><Clock size={15} /> {s.time}</span>
                          <span className="text-xs">Ocupado</span>
                        </div>
                      );
                    }
                    if (!b) {
                      return (
                        <button
                          key={s.time}
                          onClick={() => openAdd(s.time)}
                          data-testid={`free-slot-${s.time}`}
                          className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-dashed border-[#D4D4D4] hover:border-[#E20074] hover:bg-[#fdf3f8] transition-colors group"
                        >
                          <span className="flex items-center gap-2 text-[#0A0A0A] font-medium"><Clock size={15} className="text-[#525252]" /> {s.time}</span>
                          <span className="flex items-center gap-1 text-sm text-[#525252] group-hover:text-[#E20074]"><Plus size={15} /> Marcar</span>
                        </button>
                      );
                    }
                    const meta = STATUS_META[b.status] || STATUS_META.confirmed;
                    return (
                      <div key={s.time} data-testid={`booked-slot-${s.time}`} className="px-4 py-3 rounded-lg border border-[#0A0A0A]/10 bg-[#F4F4F0]">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-2 font-display font-bold"><Clock size={15} className="text-[#525252]" /> {b.time}–{endTime(b.time, b.duration)} · {b.service}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
                        </div>
                        <div className="mt-2 text-sm text-[#525252] space-y-0.5">
                          <p className="text-[#0A0A0A] font-medium">{b.owner_name} <span className="text-[#a3a3a3] font-normal">· {durLabel(b.duration)}</span></p>
                          {b.phone && <p className="flex items-center gap-1.5"><Phone size={12} /> {b.phone}</p>}
                          {b.dog_name && <p className="flex items-center gap-1.5"><Dog size={12} /> {b.dog_name}</p>}
                          {b.notes && <p className="italic">“{b.notes}”</p>}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" className="h-7 border-[#0A0A0A]/30" onClick={() => setEditing(b)} data-testid={`edit-${b.id}`}><Pencil size={13} className="mr-1" />Editar</Button>
                          {b.status !== "confirmed" && (
                            <Button size="sm" className="h-7 bg-[#00B4D8] hover:bg-[#0098b8] text-[#003]" onClick={() => setStatus(b.id, "confirmed")} data-testid={`confirm-${b.id}`}><Check size={13} className="mr-1" />Confirmar</Button>
                          )}
                          {b.status !== "done" && (
                            <Button size="sm" variant="outline" className="h-7" onClick={() => setStatus(b.id, "done")} data-testid={`done-${b.id}`}>Concluída</Button>
                          )}
                          <Button size="sm" variant="outline" className="h-7 text-[#b26a00]" onClick={() => setStatus(b.id, "cancelled")} data-testid={`cancel-${b.id}`}><X size={13} className="mr-1" />Cancelar</Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-7 text-[#525252] hover:text-[#E20074]" data-testid={`delete-${b.id}`}><Trash2 size={13} /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Eliminar marcação?</AlertDialogTitle>
                                <AlertDialogDescription>Esta ação não pode ser revertida.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction className="bg-[#E20074] hover:bg-[#c00063]" onClick={() => remove(b.id)} data-testid={`confirm-delete-${b.id}`}>Eliminar</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <AddBookingDialog open={addOpen} onOpenChange={setAddOpen} time={presetTime} dateLabel={selectedKey ? fmtDatePt(selectedKey) : ""} onSubmit={createBooking} services={SERVICES} />
      <EditBookingDialog booking={editing} onClose={() => setEditing(null)} dateLabel={selectedKey ? fmtDatePt(selectedKey) : ""} daySlots={daySlots} onSubmit={editBooking} services={SERVICES} />
    </AdminLayout>
  );
}

function AddBookingDialog({ open, onOpenChange, time, dateLabel, onSubmit, services }) {
  const [client, setClient] = useState(null);
  const [ownerName, setOwnerName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState(services[2]);
  const [duration, setDuration] = useState(60);
  const [price, setPrice] = useState("");
  const [dogName, setDogName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setClient(null); setOwnerName(""); setPhone(""); setService(services[2]); setDuration(60); setPrice(""); setDogName(""); setNotes(""); }
  }, [open, services]);

  const onPickClient = (c) => {
    setClient(c);
    if (c) {
      setOwnerName(c.owner_name || "");
      setPhone(c.phone || "");
      setDogName(c.dog_name || "");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!ownerName.trim()) return;
    setSaving(true);
    await onSubmit({
      owner_name: ownerName, phone, service, duration, dog_name: dogName, notes,
      client_id: client?.id || null,
      price: parseFloat(price) || 0,
    });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="add-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Nova marcação</DialogTitle>
          <DialogDescription className="capitalize">
            {dateLabel} · {time}{time ? `–${endTime(time, duration)}` : ""}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-booking-form">
          <div className="space-y-1.5">
            <Label>Cliente (opcional)</Label>
            <ClientPicker value={client} onChange={onPickClient} testid="ab-client-picker" />
            <p className="text-xs text-[#525252]">Se ligar a uma ficha, ao marcar como <b>Concluída</b> a visita e o valor entram automaticamente no histórico do cliente.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ab-name">Nome do cliente *</Label>
            <Input id="ab-name" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} data-testid="ab-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ab-phone">Telefone</Label>
              <Input id="ab-phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="ab-phone" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ab-dog">Cão</Label>
              <Input id="ab-dog" value={dogName} onChange={(e) => setDogName(e.target.value)} data-testid="ab-dog" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ab-service">Serviço</Label>
              <Input id="ab-service" list="ab-services" value={service} onChange={(e) => setService(e.target.value)} data-testid="ab-service" />
              <datalist id="ab-services">{services.map((s) => <option key={s} value={s} />)}</datalist>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ab-price">Valor (€)</Label>
              <Input id="ab-price" type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" data-testid="ab-price" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Duração do serviço</Label>
            <div className="flex flex-wrap gap-2" data-testid="ab-durations">
              {DURATIONS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => setDuration(d.value)}
                  data-testid={`ab-duration-${d.value}`}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                    duration === d.value ? "bg-[#0A0A0A] border-[#0A0A0A] text-white" : "border-[#D4D4D4] hover:border-[#0A0A0A]"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Label htmlFor="ab-duration-custom" className="text-sm text-[#525252] whitespace-nowrap">Personalizado:</Label>
              <Input
                id="ab-duration-custom"
                type="number"
                min={30}
                step={15}
                value={duration}
                onChange={(e) => setDuration(Math.max(30, parseInt(e.target.value, 10) || 0))}
                data-testid="ab-duration-custom"
                className="w-24"
              />
              <span className="text-sm text-[#525252]">min ({durLabel(duration)})</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ab-notes">Notas</Label>
            <Textarea id="ab-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ab-notes" />
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-[#E20074] hover:bg-[#c00063] text-white" data-testid="ab-submit">
            {saving ? "A guardar…" : "Adicionar marcação"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}



function occupiedRange(start, duration) {
  if (!start) return [];
  const [h, m] = start.split(":").map(Number);
  const startM = h * 60 + m;
  const total = Math.max(30, parseInt(duration, 10) || 30);
  const out = [];
  for (let t = startM; t < startM + total; t += 30) {
    out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
  }
  return out;
}

function EditBookingDialog({ booking, onClose, dateLabel, daySlots, onSubmit, services }) {
  const [client, setClient] = useState(null);
  const [ownerName, setOwnerName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState(services[0]);
  const [duration, setDuration] = useState(60);
  const [price, setPrice] = useState("");
  const [time, setTime] = useState("");
  const [dogName, setDogName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (booking) {
      setClient(booking.client_id ? { id: booking.client_id, owner_name: booking.owner_name, phone: booking.phone, dog_name: booking.dog_name } : null);
      setOwnerName(booking.owner_name || "");
      setPhone(booking.phone || "");
      setService(booking.service || services[0]);
      setDuration(booking.duration || 60);
      setPrice(booking.price != null ? String(booking.price) : "");
      setTime(booking.time || "");
      setDogName(booking.dog_name || "");
      setNotes(booking.notes || "");
    }
  }, [booking, services]);

  if (!booking) return null;

  const onPickClient = (c) => {
    setClient(c);
    if (c) {
      setOwnerName(c.owner_name || "");
      setPhone(c.phone || "");
      setDogName(c.dog_name || "");
    }
  };

  const ownTimes = occupiedRange(booking.time, booking.duration);
  const canPick = (s) => s.available || ownTimes.includes(s.time);

  const submit = async (e) => {
    e.preventDefault();
    if (!ownerName.trim() || !time) return;
    setSaving(true);
    await onSubmit({
      owner_name: ownerName, phone, service, time, duration, dog_name: dogName, notes,
      status: booking.status,
      client_id: client?.id || null,
      price: parseFloat(price) || 0,
    });
    setSaving(false);
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="edit-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Editar marcação</DialogTitle>
          <DialogDescription className="capitalize">{dateLabel} · {time}{time ? `–${endTime(time, duration)}` : ""}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="edit-booking-form">
          <div className="space-y-1.5">
            <Label>Cliente (opcional)</Label>
            <ClientPicker value={client} onChange={onPickClient} testid="eb-client-picker" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="eb-name">Nome do cliente *</Label>
            <Input id="eb-name" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} data-testid="eb-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="eb-phone">Telefone</Label>
              <Input id="eb-phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="eb-phone" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="eb-dog">Cão</Label>
              <Input id="eb-dog" value={dogName} onChange={(e) => setDogName(e.target.value)} data-testid="eb-dog" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="eb-service">Serviço</Label>
              <Input id="eb-service" list="eb-services" value={service} onChange={(e) => setService(e.target.value)} data-testid="eb-service" />
              <datalist id="eb-services">{services.map((s) => <option key={s} value={s} />)}</datalist>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="eb-price">Valor (€)</Label>
              <Input id="eb-price" type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="eb-price" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Hora de início</Label>
            <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto" data-testid="eb-slots">
              {(daySlots?.slots || []).map((s) => {
                const pickable = canPick(s);
                return (
                  <button
                    key={s.time}
                    type="button"
                    disabled={!pickable}
                    onClick={() => setTime(s.time)}
                    data-testid={`eb-slot-${s.time}`}
                    className={`py-1.5 rounded-md text-xs border transition-colors ${
                      time === s.time ? "bg-[#0A0A0A] border-[#0A0A0A] text-white"
                      : pickable ? "border-[#D4D4D4] hover:border-[#0A0A0A]"
                      : "border-transparent text-[#c4c4c4] line-through cursor-not-allowed"
                    }`}
                  >
                    {s.time}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Duração</Label>
            <div className="flex flex-wrap gap-2" data-testid="eb-durations">
              {DURATIONS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => setDuration(d.value)}
                  data-testid={`eb-duration-${d.value}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
                    duration === d.value ? "bg-[#0A0A0A] border-[#0A0A0A] text-white" : "border-[#D4D4D4] hover:border-[#0A0A0A]"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Label htmlFor="eb-duration-custom" className="text-sm text-[#525252] whitespace-nowrap">Personalizado:</Label>
              <Input
                id="eb-duration-custom"
                type="number"
                min={30}
                step={15}
                value={duration}
                onChange={(e) => setDuration(Math.max(30, parseInt(e.target.value, 10) || 0))}
                data-testid="eb-duration-custom"
                className="w-24"
              />
              <span className="text-sm text-[#525252]">min ({durLabel(duration)})</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="eb-notes">Notas</Label>
            <Textarea id="eb-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="eb-notes" />
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-[#E20074] hover:bg-[#c00063] text-white" data-testid="eb-submit">
            {saving ? "A guardar…" : "Guardar alterações"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
